import { useId, useState } from 'react';
import { formatDate, formatMoney, formatNumber, formatShortMoney, t } from '@aquabox/shared';
import { api } from '../api/client';
import type { DepositReport, ReportSummary } from '../api/types';
import {
  useReport,
  type ReportSourceFilter,
  type ReportsResponse,
  type ProductReportRow,
  type SourceChannelRow,
  type VehicleBreakdownReport,
  type JugKindBucket,
} from '../api/reports';
import { useAbantEmployeeNames, useDepositReport } from '../api/queries';
import {
  monthOptions,
  monthlyReportFilename,
  monthlyReportPath,
  previousMonthKey,
} from '../api/monthly-report';
import {
  useOwnerSummary,
  type OwnerSummaryPeriod,
  type OwnerSummaryPeriodKey,
} from '../api/owner-summary';
import { Kpi } from '../components/Kpi';
import { CHANNEL_COLOR } from '../lib/labels';
import { periodLabel, type PeriodKey } from '../lib/periods';

/**
 * Bayi panelinde (Hayat/Abant) kapanan siparişler için kaynak rozeti. `OrderSource` sunucudan
 * büyük harf gelir (`HAYAT_PANEL`/`ABANT_PANEL`); rozet tek harf, adı i18n'den.
 */
const SOURCE_BADGE: Record<
  string,
  { letter: string; nameKey: 'panelRep.sourceHayat' | 'panelRep.sourceAbant' }
> = {
  HAYAT_PANEL: { letter: 'H', nameKey: 'panelRep.sourceHayat' },
  ABANT_PANEL: { letter: 'A', nameKey: 'panelRep.sourceAbant' },
};

const PERIODS: { value: string; period: PeriodKey }[] = [
  { value: '7d', period: 'last7' },
  { value: '30d', period: 'last30' },
  { value: 'month', period: 'thisMonth' },
];

/** Kaynak filtresi: Tümü / Abant / Hayat / Bizim. Şema `OrderSource` enum'undan türer. */
const SOURCE_FILTERS: { value: ReportSourceFilter; labelKey: Parameters<typeof t>[0] }[] = [
  { value: 'all', labelKey: 'rep.sourceAll' },
  { value: 'abant', labelKey: 'rep.sourceAbant' },
  { value: 'hayat', labelKey: 'rep.sourceHayat' },
  { value: 'ours', labelKey: 'rep.sourceOurs' },
];

/** Aktarım için elle seçilen gün/aralık; `null` ise segment butonunun dönemi kullanılır. */
type ExportRange = { from: string; to: string } | null;

/**
 * Aralığın gün üst sınırı — sunucudaki `reports.schema.ts#MAX_EXPORT_DAYS` ile AYNI olmalı.
 *
 * Panel burada karar vermez, yalnız indirilmeyeceği baştan belli olan bir isteği göndermez;
 * son söz sunucunundur (aşan aralıkta 400 döner) — `reports.e2e-spec.ts` sınırın iki yakasını
 * (tam sınır 200 · bir gün fazlası 400) sabitler.
 *
 * KOPYA BİLEREK DURUYOR: panel `@aquabox/api`'yi import edemez ve `packages/shared` altında
 * sabitleri barındıran dışa aktarılmış bir modül yok (i18n/format/roles/time/version). Ortak
 * bir yer açmak yeni dosya + `packages/shared/src/index.ts` değişikliği ister.
 */
const MAX_EXPORT_DAYS = 366;

/** Aralığın gün sayısı, iki uç DAHİL. Uçlardan biri boşsa ya da ters sıradaysa `null`. */
function rangeDays(range: ExportRange): number | null {
  if (range === null || range.from === '' || range.to === '') return null;
  const ms = Date.parse(`${range.to}T00:00:00.000Z`) - Date.parse(`${range.from}T00:00:00.000Z`);
  if (!Number.isFinite(ms)) return null;
  const days = Math.round(ms / 86_400_000) + 1;
  return days < 1 ? null : days;
}

/**
 * Raporlar (§3.16).
 *
 * Panelde hiçbir toplama yapılmaz: ciro, yüzde ve ortalama sunucuda `Decimal` ile hesaplanır
 * (kural 4). Ekran yalnız çizer.
 *
 * AKTARIM DÖNEMİ ayrı seçilebilir (Faz 8 denetimi B-07): API `?day=` (tek gün) ve `?from=&to=`
 * (aralık) kabul ediyordu ama panelde seçici yoktu; muhasebeci "geçen haftayı aktar" diyemiyor,
 * her seferinde segment butonunun dönemini indiriyordu.
 */
export function Reports() {
  const [period, setPeriod] = useState<string>('7d');
  const [source, setSource] = useState<ReportSourceFilter>('all');
  const report = useReport(period, source);
  const data = report.data;
  const deposits = useDepositReport();
  const [range, setRange] = useState<ExportRange>(null);
  // Aktarım hatası TOOLBAR'da tek yerde gösterilir; üç butonun ortak durumu.
  const [exportFailed, setExportFailed] = useState(false);
  // Ay sonu raporu (PDF): varsayılan ay KAPANMIŞ son aydır (içinde bulunulan ay henüz kapanmadı).
  const [reportMonth, setReportMonth] = useState<string>(() => previousMonthKey());
  const [monthlyFailed, setMonthlyFailed] = useState(false);

  // Kutular boşken seçili dönemin günlerini gösterir: ekranda ne yazıyorsa o aktarılır.
  const from = range?.from ?? data?.period.fromDay ?? '';
  const to = range?.to ?? data?.period.toDay ?? '';
  const days = rangeDays(range);
  const rangeOk = range === null || (days !== null && days <= MAX_EXPORT_DAYS);
  // Üç yol birbirini DIŞLAR (bkz. reports.schema.ts): ya `period`, ya `day`, ya `from&to`.
  const exportQs =
    range !== null && rangeOk
      ? range.from === range.to
        ? `day=${range.from}`
        : `from=${range.from}&to=${range.to}`
      : `period=${period}`;

  return (
    <div className="page">
      <OwnerSummarySection />
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {PERIODS.map((p) => (
          <button
            key={p.value}
            onClick={() => {
              setPeriod(p.value);
              // Segment butonu dönemi devralır; elle seçilen aralık kalırsa ekranla aktarım ayrışır.
              setRange(null);
            }}
            aria-pressed={period === p.value}
            style={{
              borderRadius: 'var(--r-pill)',
              padding: '10px 15px',
              fontWeight: 600,
              fontSize: 13,
              background: period === p.value ? 'var(--primary)' : 'var(--surface)',
              border: `1px solid ${period === p.value ? 'var(--primary)' : 'var(--border)'}`,
              color: period === p.value ? '#fff' : 'var(--text-secondary)',
            }}
          >
            {periodLabel(p.period)}
          </button>
        ))}
        {/*
          Kaynak filtresi: Tümü / Abant / Hayat / Bizim. Üst başlık YOK, segment butonları
          kompakt; seçili olan tek renkle ayrılır. Patronun "Hayat'ta kaç tane?" sorusu bu
          segmente basıp cevabı bulması içindir.
        */}
        <span
          role="group"
          aria-label={t('rep.sourceLabel')}
          style={{ display: 'flex', gap: 6, alignItems: 'center' }}
        >
          <span style={{ fontSize: 12, color: 'var(--text-secondary)', marginRight: 4 }}>
            {t('rep.sourceLabel')}:
          </span>
          {SOURCE_FILTERS.map((s) => (
            <button
              key={s.value}
              onClick={() => setSource(s.value)}
              aria-pressed={source === s.value}
              style={{
                borderRadius: 'var(--r-pill)',
                padding: '8px 12px',
                fontWeight: 600,
                fontSize: 12,
                background: source === s.value ? 'var(--primary)' : 'var(--surface)',
                border: `1px solid ${source === s.value ? 'var(--primary)' : 'var(--border)'}`,
                color: source === s.value ? '#fff' : 'var(--text-secondary)',
              }}
            >
              {t(s.labelKey)}
            </button>
          ))}
        </span>
        {/*
          Gün/aralık seçici. Kutular seçili dönemin günleriyle dolu gelir; biri değiştirilince
          aktarım o aralığa geçer, iki uç aynı günse tek gün (`day=`) istenir. "Temizle" segment
          butonunun dönemine döndürür.
        */}
        <span
          role="group"
          aria-label={t('rep.thisPeriod')}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <input
            type="date"
            className="input"
            aria-label={t('rep.dayFrom')}
            value={from}
            onChange={(e) => setRange({ from: e.target.value, to })}
            style={{ height: 38, fontSize: 13, width: 150 }}
          />
          <span style={{ color: 'var(--text-secondary)' }}>–</span>
          <input
            type="date"
            className="input"
            aria-label={t('rep.dayTo')}
            value={to}
            min={from || undefined}
            onChange={(e) => setRange({ from, to: e.target.value })}
            style={{ height: 38, fontSize: 13, width: 150 }}
          />
          {range !== null ? (
            <button
              className="btn"
              onClick={() => setRange(null)}
              style={{ fontSize: 12.5, padding: '8px 12px' }}
            >
              {t('panel.clearFilters')}
            </button>
          ) : null}
        </span>
        {rangeOk ? null : (
          // Sunucu da aynı sınırı uygular; buton pasif bırakılır ki boş yere indirme denenmesin.
          <span style={{ fontSize: 12.5, color: 'var(--danger)' }}>
            {t('rep.rangeTooWide', { n: MAX_EXPORT_DAYS })}
          </span>
        )}
        {/* İndirme 4xx/5xx alırsa kullanıcı hiçbir şey görmüyordu; hata SESSİZ kalmaz. */}
        {exportFailed ? (
          <span style={{ fontSize: 12.5, color: 'var(--danger)' }}>{t('rep.exportFailed')}</span>
        ) : null}
        <span style={{ flex: 1 }} />
        <Download
          path={`/api/reports/export/luca?${exportQs}`}
          label={t('rep.exportLuca')}
          description={t('rep.descLuca')}
          disabled={!rangeOk}
          onFailed={setExportFailed}
          primary
        />
        <Download
          path={`/api/reports/export/trips?${exportQs}`}
          label={t('rep.exportTrips')}
          description={t('rep.descTrips')}
          disabled={!rangeOk}
          onFailed={setExportFailed}
        />
        <Download
          path="/api/reports/export/customers"
          label={t('rep.exportCustomers')}
          description={t('rep.descCustomers')}
          onFailed={setExportFailed}
        />
      </div>

      {/*
        Ay sonu raporu (PDF). Kapanmış ay seçilir; varsayılan ay içinde bulunulan ay DEĞİL,
        TAMAMLANMIŞ son aydır (ay sonu raporu kapanmamış ayı anlatmaz). Raporun İÇERİĞİ ve
        para hesabı sunucudadır (kural 4): panel yalnız indirir.
      */}
      <section
        className="card"
        style={{
          padding: 20,
          marginTop: 16,
          display: 'flex',
          gap: 18,
          alignItems: 'flex-end',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div className="section-label">{t('monthlyReport.title')}</div>
          <label
            style={{ fontSize: 12, color: 'var(--text-secondary)' }}
            htmlFor="monthly-report-month"
          >
            {t('monthlyReport.selectMonth')}
          </label>
          <select
            id="monthly-report-month"
            className="input"
            aria-label={t('monthlyReport.selectMonth')}
            value={reportMonth}
            onChange={(e) => {
              setReportMonth(e.target.value);
              // Yeni ay seçilince eski hata mesajı yanlış ayı gösterir.
              setMonthlyFailed(false);
            }}
            style={{ height: 38, fontSize: 13, width: 170 }}
          >
            {monthOptions().map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
        <Download
          path={monthlyReportPath(reportMonth)}
          label={t('monthlyReport.download')}
          description={t('monthlyReport.description')}
          filename={monthlyReportFilename(reportMonth)}
          onFailed={setMonthlyFailed}
        />
        {monthlyFailed ? (
          <span style={{ fontSize: 12.5, color: 'var(--danger)' }}>
            {t('monthlyReport.downloadFailed')}
          </span>
        ) : null}
      </section>

      <section className="kpi-grid">
        <Kpi
          label={t('rep.totalSales')}
          value={formatMoney(data?.kpi.combinedRevenue.total ?? '0')}
          note={
            data
              ? [
                  t('panelRep.revenueFootnote', {
                    ours: formatMoney(data.kpi.combinedRevenue.ours),
                    panel: formatMoney(data.kpi.combinedRevenue.panelDelivered),
                  }),
                  data.kpi.combinedRevenue.changePct === null
                    ? null
                    : t('rep.vsPrev', {
                        n: `${data.kpi.combinedRevenue.changePct > 0 ? '+' : ''}${data.kpi.combinedRevenue.changePct}%`,
                      }),
                ]
                  .filter(Boolean)
                  .join(' · ')
              : undefined
          }
        />
        <Kpi
          label={t('rep.deliveredOrders')}
          value={data?.kpi.combinedDeliveries ?? 0}
          note={
            data
              ? t('panelRep.deliveriesFootnote', {
                  ours: data.kpi.deliveries,
                  panel: data.kpi.combinedDeliveries - data.kpi.deliveries,
                  perDay: data.kpi.deliveriesPerDay,
                })
              : undefined
          }
        />
        <Kpi
          label={t('rep.jugs')}
          value={data?.kpi.combinedJugs ?? 0}
          note={
            (data?.kpi.jugsByProduct ?? [])
              .map((p) => `${p.name} ${p.oursQty} + ${p.panelQty} = ${p.totalQty}`)
              .join(' · ') || undefined
          }
        />
        <Kpi
          label={t('rep.averageOrder')}
          value={formatMoney(data?.kpi.combinedAverageBasket ?? '0')}
        />
      </section>

      <details className="card" style={{ padding: '12px 20px', marginTop: -6 }}>
        <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
          {t('rep.moneyExplainerTitle')}
        </summary>
        <ul style={{ margin: '10px 0 0', paddingLeft: 18, fontSize: 13, lineHeight: 1.6 }}>
          <li>{t('rep.moneyExplainerOurs')}</li>
          <li>{t('rep.moneyExplainerPanel')}</li>
          <li>{t('rep.moneyExplainerExcluded')}</li>
          <li>{t('rep.moneyExplainerDeposit')}</li>
          <li>{t('rep.moneyExplainerCollection')}</li>
        </ul>
      </details>

      <section
        className="section-grid"
        style={{ '--section-cols': '2fr 1fr' } as React.CSSProperties}
      >
        <div className="card" style={{ padding: 20 }}>
          <div className="section-label" style={{ marginBottom: 14 }}>
            {t('rep.dailyRevenue')}
          </div>
          <DailyChart rows={data?.daily ?? []} />
        </div>
        <div className="card" style={{ padding: 20 }}>
          <div className="section-label" style={{ marginBottom: 14 }}>
            {t('rep.channelShare')}
          </div>
          <ChannelShare rows={data?.byChannelTotal ?? data?.byChannel ?? []} />
        </div>
      </section>

      <section className="section-grid">
        <DriverTable rows={data?.byDriver ?? []} />
        <div className="card" style={{ padding: 20 }}>
          <div className="section-label" style={{ marginBottom: 14 }}>
            {t('rep.hourly')}
          </div>
          <HourlyChart rows={data?.byHour ?? []} />
        </div>
      </section>

      <section className="card card-pad">
        <div className="section-label" style={{ marginBottom: 12 }}>
          {t('panelRep.productTableTitle')}
        </div>
        <ProductByChannelTable rows={data?.kpi.jugsByProduct ?? []} />
      </section>

      <section className="card" style={{ padding: 20 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginBottom: 6,
          }}
        >
          <div className="section-label">{t('panelRep.sectionTitle')}</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {t('rep.channelHandledHint')}
          </div>
        </div>
        {data ? (
          <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 4 }}>
            {t('panelRep.combinedRevenueThree', {
              ours: formatMoney(data.kpi.combinedRevenue.ours),
              panel: formatMoney(data.kpi.combinedRevenue.panelDelivered),
              total: formatMoney(data.kpi.combinedRevenue.total),
            })}
          </div>
        ) : null}
        <ChannelHandledTable data={data?.channelHandled} />
      </section>

      <section className="card" style={{ padding: 20 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginBottom: 8,
            flexWrap: 'wrap',
            gap: 6,
          }}
        >
          <div className="section-label">{t('rep.sourceChannelTitle')}</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {t('rep.sourceChannelHint')}
          </div>
        </div>
        <SourceChannelTable rows={data?.sourceChannelTable ?? []} />
        <TableCsvButton
          filename={`kaynak_kanal_${data?.period.fromDay ?? ''}_${data?.period.toDay ?? ''}.csv`}
          label={t('rep.exportSourceChannel')}
          description={t('rep.descSourceChannel')}
          disabled={!data?.sourceChannelTable?.length}
          rows={csvFromSourceChannel(data?.sourceChannelTable ?? [])}
        />
      </section>

      <section className="card" style={{ padding: 20 }}>
        <div className="section-label" style={{ marginBottom: 8 }}>
          {t('rep.productTableTitleNew')}
        </div>
        <ProductTable rows={data?.productTable ?? []} />
        <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 10 }}>
          {t('rep.productTableNote')}
        </p>
        <TableCsvButton
          filename={`urunler_${data?.period.fromDay ?? ''}_${data?.period.toDay ?? ''}.csv`}
          label={t('rep.exportProducts')}
          description={t('rep.descProducts')}
          disabled={!data?.productTable?.length}
          rows={csvFromProducts(data?.productTable ?? [])}
        />
      </section>

      <section className="card" style={{ padding: 20 }}>
        <div className="section-label" style={{ marginBottom: 8 }}>
          {t('rep.jugsByKindTitle')}
        </div>
        <JugsByKindTable data={data?.jugsByKind} />
        <TableCsvButton
          filename={`damacana_${data?.period.fromDay ?? ''}_${data?.period.toDay ?? ''}.csv`}
          label={t('rep.exportJugs')}
          description={t('rep.descJugs')}
          disabled={!data?.jugsByKind}
          rows={csvFromJugs(data?.jugsByKind)}
        />
      </section>

      <section className="card" style={{ padding: 20 }}>
        <div className="section-label" style={{ marginBottom: 8 }}>
          {t('rep.vehicleTitle')}
        </div>
        <VehicleTable data={data?.vehicles} />
        <TableCsvButton
          filename={`araclar_${data?.period.fromDay ?? ''}_${data?.period.toDay ?? ''}.csv`}
          label={t('rep.exportVehicles')}
          description={t('rep.descVehicles')}
          disabled={!data?.vehicles}
          rows={csvFromVehicles(data?.vehicles)}
        />
      </section>

      <DepositSection data={deposits.data} failed={deposits.isError} />
    </div>
  );
}

/**
 * Dışa aktarım butonu: token bellekte olduğu için düz bağlantı çalışmaz, blob indirilir.
 *
 * `getBlobUrl` 4xx/5xx'te fırlatır (bkz. `api/client.ts`); yakalanmadığı sürece indirme
 * başarısız olduğunda ekranda HİÇBİR ŞEY değişmiyordu — buton eski haline dönüyor, kullanıcı
 * dosyanın indiğini sanıyordu. Hata `onFailed` ile üst bileşene bildirilir (Cash.tsx#PdfButton
 * ile aynı desen: yakala, kullanıcıya Türkçe göster).
 */
function Download({
  path,
  label,
  description,
  filename,
  primary,
  disabled,
  onFailed,
}: {
  path: string;
  label: string;
  description: string;
  /** İndirilen dosyanın adı. Verilmezse tarayıcı sunucunun adını kullanır. */
  filename?: string;
  primary?: boolean;
  disabled?: boolean;
  onFailed: (failed: boolean) => void;
}) {
  const [busy, setBusy] = useState(false);
  const descId = useId();
  async function run(): Promise<void> {
    setBusy(true);
    onFailed(false);
    try {
      const url = await api.getBlobUrl(path);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename ?? '';
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      onFailed(true);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 240 }}>
      <button
        className={primary ? 'btn btn-primary' : 'btn'}
        disabled={busy || disabled}
        aria-describedby={descId}
        onClick={() => {
          void run();
        }}
      >
        {label}
      </button>
      <span id={descId} style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
        {description}
      </span>
    </div>
  );
}

/**
 * Günlük satış grafiği: bizim teslimat (`revenue`) altta, panelde kapanan (`panelRevenue`) üstte
 * yığılır; yanındaki gri çubuk önceki dönemin aynı toplamıdır (`previousTotalRevenue`). Toplamlar
 * API'den gelir; burada yalnız çubuk yüksekliği için oranlanır.
 */
function DailyChart({ rows }: { rows: ReportSummary['daily'] }) {
  const max = Math.max(
    1,
    ...rows.flatMap((r) => [Number(r.totalRevenue), Number(r.previousTotalRevenue)]),
  );
  if (rows.length === 0) return <Empty />;
  const px = (amount: string): number => (Number(amount) / max) * 150;
  return (
    <>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', height: 210 }}>
        {rows.map((r) => (
          <div key={r.day} style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: 11.5, fontWeight: 600, marginBottom: 4 }}>
              {formatShortMoney(r.totalRevenue)}
            </div>
            <div style={{ display: 'flex', gap: 3, alignItems: 'flex-end', height: 150 }}>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {Number(r.panelRevenue) > 0 ? (
                  <div
                    data-testid="bar-panel"
                    style={{
                      height: px(r.panelRevenue),
                      background: 'var(--warning)',
                      borderRadius: '4px 4px 0 0',
                    }}
                  />
                ) : null}
                <div
                  style={{
                    height: Math.max(2, px(r.revenue)),
                    background: 'var(--primary)',
                    borderRadius: Number(r.panelRevenue) > 0 ? 0 : '4px 4px 0 0',
                  }}
                />
              </div>
              <Bar height={px(r.previousTotalRevenue)} color="var(--border)" />
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 6 }}>
              {r.day.slice(8)}.{r.day.slice(5, 7)}
            </div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 14, marginTop: 10, fontSize: 12, flexWrap: 'wrap' }}>
        <Legend color="var(--primary)" label={t('rep.dailyOursLegend')} />
        <Legend color="var(--warning)" label={t('rep.dailyPanelLegend')} />
        <Legend color="var(--border)" label={t('rep.prevPeriod')} />
      </div>
    </>
  );
}

function ChannelShare({ rows }: { rows: ReportSummary['byChannel'] }) {
  if (rows.length === 0) return <Empty />;
  let offset = 0;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'center' }}>
      <svg
        viewBox="0 0 42 42"
        style={{ width: 150, height: 150 }}
        role="img"
        aria-label={t('rep.channelShare')}
      >
        <circle cx="21" cy="21" r="15.9" fill="none" stroke="var(--page-bg)" strokeWidth="6" />
        {rows.map((r) => {
          const dash = `${r.sharePct} ${100 - r.sharePct}`;
          const rotation = -25 - offset * 3.6;
          offset += r.sharePct;
          return (
            <circle
              key={r.channel}
              cx="21"
              cy="21"
              r="15.9"
              fill="none"
              stroke={CHANNEL_COLOR[r.channel] ?? 'var(--primary)'}
              strokeWidth="6"
              strokeDasharray={dash}
              transform={`rotate(${rotation} 21 21)`}
            />
          );
        })}
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
        {rows.map((r) => (
          <div
            key={r.channel}
            style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: 3,
                background: CHANNEL_COLOR[r.channel] ?? 'var(--primary)',
              }}
            />
            <span style={{ flex: 1 }}>
              {t(`orderChannel.${r.channel}` as Parameters<typeof t>[0])}
            </span>
            <span style={{ fontWeight: 600 }}>{r.sharePct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Ürün bazlı damacana kırılımı. Kural 4: panelde hesap YOK — `oursQty`/`panelQty`/`totalQty`
 * sunucudan gelir; burada yalnız çizilir.
 *
 * Üç sütun: bizim teslimat (ciro defteri), panelde teslim (Hayat/Abant), toplam. Toplam
 * yalnız görsel amaçlı değil: kullanıcı "bugün kaç damacana çıktı" sorusuna tek sayıyla cevap
 * alır (sunum B-01 geri bildirimi).
 */
function ProductByChannelTable({ rows }: { rows: ReportSummary['kpi']['jugsByProduct'] }) {
  if (rows.length === 0) return <Empty />;
  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '2fr 90px 90px 90px',
          padding: '0 0 8px',
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--text-secondary)',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <span>{t('stock.colProduct')}</span>
        <span style={{ textAlign: 'right' }}>{t('panelRep.productColOurs')}</span>
        <span style={{ textAlign: 'right' }}>{t('panelRep.productColPanel')}</span>
        <span style={{ textAlign: 'right' }}>{t('panelRep.productColTotal')}</span>
      </div>
      {rows.map((r) => (
        <div
          key={r.productId}
          style={{
            display: 'grid',
            gridTemplateColumns: '2fr 90px 90px 90px',
            padding: '10px 0',
            borderBottom: '1px solid var(--border)',
            fontSize: 13,
          }}
        >
          <span style={{ fontWeight: 500 }}>{r.name}</span>
          <span style={{ textAlign: 'right' }}>{r.oursQty}</span>
          <span style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{r.panelQty}</span>
          <span style={{ textAlign: 'right', fontWeight: 600 }}>{r.totalQty}</span>
        </div>
      ))}
    </div>
  );
}

function DriverTable({ rows }: { rows: ReportSummary['byDriver'] }) {
  return (
    <div className="card" style={{ overflow: 'auto' }}>
      <div className="table-head reports-driver-grid" style={{ padding: '0 20px', height: 44 }}>
        <span>{t('rep.colDriver')}</span>
        <span className="col-hide-sm" style={{ textAlign: 'right' }}>
          {t('rep.colDeliveries')}
        </span>
        <span className="col-hide-sm" style={{ textAlign: 'right' }}>
          {t('rep.colJugs')}
        </span>
        <span style={{ textAlign: 'right' }}>{t('rep.colCollected')}</span>
        <span>{t('rep.colEquation')}</span>
      </div>
      {rows.length === 0 ? (
        <p style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--text-secondary)' }}>
          {t('rep.noData')}
        </p>
      ) : (
        rows.map((r) => (
          <div
            key={r.driverId}
            className="reports-driver-grid"
            style={{ padding: '0 20px', height: 56, borderTop: '1px solid var(--border)' }}
          >
            <span>
              <span style={{ display: 'block', fontWeight: 500 }}>{r.name}</span>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{r.plate}</span>
            </span>
            <span className="col-hide-sm" style={{ textAlign: 'right' }}>
              {r.deliveries}
            </span>
            <span className="col-hide-sm" style={{ textAlign: 'right' }}>
              {r.jugs}
            </span>
            <span style={{ textAlign: 'right', fontWeight: 600 }}>{formatMoney(r.collected)}</span>
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: r.varianceTrips > 0 ? 'var(--warning)' : 'var(--success)',
              }}
            >
              {r.varianceTrips > 0
                ? t('rep.varianceDays', { n: r.varianceTrips })
                : t('rep.matched')}
            </span>
          </div>
        ))
      )}
    </div>
  );
}

/**
 * Kanal panelinde (Hayat/Abant) kapanan siparişlerin kırılımı.
 *
 * Tüm toplam + para değerleri API'den gelir (kural 4): panelde hesap YOK. Sunucu
 * `externalProductAmount` doluysa kanal fiyatını, dolu değilse null döner; null'da sütun
 * boş kalır (yorum yok, sıfır yazıp "Hayat kanal fiyatı = 0" gibi yanlış bir izlenim
 * vermek yerine).
 */
function ChannelHandledTable({ data }: { data: ReportSummary['channelHandled'] | undefined }) {
  if (!data || data.totalCount === 0) {
    return (
      <p
        style={{
          padding: '20px 0',
          fontSize: 13.5,
          color: 'var(--text-secondary)',
          textAlign: 'center',
        }}
      >
        {t('rep.channelHandledEmpty')}
      </p>
    );
  }
  return (
    <>
      <div
        className="channel-handled-grid"
        style={{
          padding: '0 0 8px',
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--text-secondary)',
        }}
      >
        <span>{t('rep.colChannel')}</span>
        <span style={{ textAlign: 'right' }}>{t('rep.colOrderCount')}</span>
        <span style={{ textAlign: 'right' }}>{t('rep.colChannelAmount')}</span>
        <span style={{ textAlign: 'right' }}>{t('rep.colCatalogAmount')}</span>
      </div>
      {data.byChannel.map((r) => (
        <div
          key={r.channel}
          className="channel-handled-grid"
          style={{
            padding: '10px 0',
            borderTop: '1px solid var(--border)',
            fontSize: 13,
          }}
        >
          <span>
            <span
              style={{
                display: 'inline-block',
                width: 10,
                height: 10,
                borderRadius: 3,
                background: CHANNEL_COLOR[r.channel] ?? 'var(--primary)',
                marginRight: 8,
                verticalAlign: 'middle',
              }}
            />
            {t(`orderChannel.${r.channel}` as Parameters<typeof t>[0])}
          </span>
          <span style={{ textAlign: 'right' }}>{r.count}</span>
          <span style={{ textAlign: 'right', fontWeight: 600 }}>{formatMoney(r.totalAmount)}</span>
          <span style={{ textAlign: 'right', color: 'var(--text-secondary)', fontSize: 12 }}>
            {formatMoney(r.catalogAmount)}
          </span>
        </div>
      ))}
      <div
        className="channel-handled-grid"
        style={{
          padding: '12px 0 0',
          marginTop: 6,
          borderTop: '2px solid var(--border)',
          fontSize: 13.5,
          fontWeight: 600,
        }}
      >
        <span>{t('rep.colTotal')}</span>
        <span style={{ textAlign: 'right' }}>{data.totalCount}</span>
        <span style={{ textAlign: 'right' }}>{formatMoney(data.totalAmount)}</span>
        <span style={{ textAlign: 'right', color: 'var(--text-secondary)', fontSize: 12 }}>
          {formatMoney(data.catalogAmount)}
        </span>
      </div>
      {data.bySource.length > 0 ? (
        <div style={{ marginTop: 18 }}>
          <div className="section-label" style={{ marginBottom: 8 }}>
            {t('panelRep.sourceBreakdownTitle')}
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1.4fr 80px 80px 80px 130px',
              padding: '0 0 6px',
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--text-secondary)',
            }}
          >
            <span>{t('panelRep.colSource')}</span>
            <span style={{ textAlign: 'right' }}>{t('panelRep.colDelivered')}</span>
            <span style={{ textAlign: 'right' }}>{t('panelRep.colCancelled')}</span>
            <span style={{ textAlign: 'right' }}>{t('panelRep.colPending')}</span>
            <span style={{ textAlign: 'right' }}>{t('rep.colChannelAmount')}</span>
          </div>
          {data.bySource.map((r) => {
            const badge = SOURCE_BADGE[r.source];
            return (
              <div
                key={r.source}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1.4fr 80px 80px 80px 130px',
                  padding: '8px 0',
                  borderTop: '1px solid var(--border)',
                  fontSize: 13,
                  alignItems: 'center',
                }}
              >
                <span>
                  {badge ? (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 22,
                        height: 22,
                        marginRight: 8,
                        borderRadius: 4,
                        background: 'var(--primary-bg, var(--primary))',
                        color: 'var(--primary)',
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {badge.letter}
                    </span>
                  ) : null}
                  {badge ? t(badge.nameKey) : r.source}
                </span>
                <span style={{ textAlign: 'right', fontWeight: 600 }}>{r.deliveredCount}</span>
                <span style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>
                  {r.cancelledCount}
                </span>
                <span style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>
                  {r.pendingCount}
                </span>
                <span style={{ textAlign: 'right', fontWeight: 600 }}>
                  {formatMoney(r.deliveredAmount)}
                </span>
              </div>
            );
          })}
        </div>
      ) : null}
      {data.byPaymentType.length > 0 ? (
        <div style={{ marginTop: 18 }}>
          <div className="section-label" style={{ marginBottom: 8 }}>
            {t('hayatRep.paymentTypeTitle')}
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 80px 130px',
              padding: '0 0 6px',
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--text-secondary)',
            }}
          >
            <span>{t('hayatRep.colPayment')}</span>
            <span style={{ textAlign: 'right' }}>{t('rep.colOrderCount')}</span>
            <span style={{ textAlign: 'right' }}>{t('rep.colChannelAmount')}</span>
          </div>
          {data.byPaymentType.map((r) => (
            <div
              key={r.paymentType}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 80px 130px',
                padding: '8px 0',
                borderTop: '1px solid var(--border)',
                fontSize: 13,
              }}
            >
              <span>{t(`paymentType.${r.paymentType}` as Parameters<typeof t>[0])}</span>
              <span style={{ textAlign: 'right' }}>{r.count}</span>
              <span style={{ textAlign: 'right', fontWeight: 600 }}>
                {formatMoney(r.totalAmount)}
              </span>
            </div>
          ))}
        </div>
      ) : null}
      {data.jugs.length > 0 ? (
        <div style={{ marginTop: 14, fontSize: 13 }}>
          <span style={{ fontWeight: 600 }}>{t('hayatRep.jugsLabel')}: </span>
          {jugsLine(data.jugs)}
        </div>
      ) : null}
    </>
  );
}

/** "Damacana × qty" satırları " · " ile birleşir; sunucu türetir, panel yalnız çizer. */
function jugsLine(jugs: ReportSummary['channelHandled']['jugs']): string {
  return jugs
    .map((j) => `${t(`productKind.${j.kind}` as Parameters<typeof t>[0])} × ${j.qty}`)
    .join(' · ');
}

function HourlyChart({ rows }: { rows: ReportSummary['byHour'] }) {
  // Sahada anlamlı aralık: 08–19. Gece kovaları grafiği boş yere sıkıştırıyor.
  const window = rows.filter((r) => r.hour >= 8 && r.hour <= 19);
  const max = Math.max(1, ...window.map((r) => r.count));
  const busiest = window.reduce((best, r) => (r.count > best.count ? r : best), {
    hour: 12,
    count: 0,
  });
  if (window.every((r) => r.count === 0)) return <Empty />;
  return (
    <>
      <div style={{ display: 'flex', gap: 5, alignItems: 'flex-end', height: 150 }}>
        {window.map((r) => (
          <div key={r.hour} style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ display: 'flex', height: 120, alignItems: 'flex-end' }}>
              <Bar
                height={(r.count / max) * 120}
                color={r.count >= max * 0.88 ? 'var(--primary)' : 'var(--primary-light)'}
              />
            </div>
            <div style={{ fontSize: 10.5, color: 'var(--text-secondary)', marginTop: 5 }}>
              {r.hour}
            </div>
          </div>
        ))}
      </div>
      <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 10, lineHeight: 1.5 }}>
        {t('rep.busiest', { from: busiest.hour, to: busiest.hour + 1 })}
      </p>
    </>
  );
}

function Bar({ height, color }: { height: number; color: string }) {
  return (
    <div
      style={{
        flex: 1,
        height: Math.max(2, height),
        background: color,
        borderRadius: '4px 4px 0 0',
      }}
    />
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 10, height: 10, borderRadius: 3, background: color }} />
      {label}
    </span>
  );
}

/**
 * Zimmet / depozito kartı (patron): kimde kaç damacana zimmetli, karşılığında ne kadar depozito
 * alınmış. Toplamlar ve tutarlar sunucudan gelir; panel yalnız biçimlendirir (kural 4).
 * Depozito ürüne bağlı olmadığı için ürün kırılımında tire basılır.
 */
function DepositSection({ data, failed }: { data: DepositReport | undefined; failed: boolean }) {
  return (
    <section className="card" style={{ padding: 20 }}>
      <div className="section-label" style={{ marginBottom: 4 }}>
        {t('depositReport.title')}
      </div>
      <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 4px' }}>
        {t('depositReport.subtitle')}
      </p>
      <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 12px' }}>
        {t('depositReport.panelNote')}
      </p>
      {failed ? (
        <p style={{ fontSize: 13, color: 'var(--danger)', margin: 0 }}>
          {t('depositReport.loadError')}
        </p>
      ) : !data ? (
        <Empty />
      ) : (
        <>
          <section className="kpi-grid" style={{ marginBottom: 16 }}>
            <Kpi
              label={t('depositReport.jugsOnHand')}
              value={formatNumber(data.totals.jugsOnHand)}
            />
            <Kpi
              label={t('depositReport.depositHeld')}
              value={formatMoney(data.totals.depositHeld)}
            />
            <Kpi label={t('depositReport.customers')} value={formatNumber(data.totals.customers)} />
          </section>

          {data.byProduct.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div className="section-label" style={{ fontSize: 13, marginBottom: 6 }}>
                {t('depositReport.byProductTitle')}
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 110px',
                  padding: '0 0 6px',
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--text-secondary)',
                }}
              >
                <span>{t('depositReport.colProduct')}</span>
                <span style={{ textAlign: 'right' }}>{t('depositReport.colJugs')}</span>
              </div>
              {data.byProduct.map((p) => (
                <div
                  key={p.productId}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 110px',
                    padding: '8px 0',
                    borderTop: '1px solid var(--border)',
                    fontSize: 13,
                  }}
                >
                  <span style={{ fontWeight: 500 }}>{p.name}</span>
                  <span style={{ textAlign: 'right', fontWeight: 600 }}>
                    {formatNumber(p.jugsOnHand)}
                  </span>
                </div>
              ))}
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8 }}>
                {t('depositReport.productNote')}
              </p>
            </div>
          )}

          {data.items.length === 0 ? (
            <p style={{ fontSize: 13.5, color: 'var(--text-secondary)', margin: 0 }}>
              {t('depositReport.empty')}
            </p>
          ) : (
            <div>
              <div className="section-label" style={{ fontSize: 13, marginBottom: 6 }}>
                {t('depositReport.customersTitle')}
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 110px 130px 130px',
                  padding: '0 0 6px',
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--text-secondary)',
                }}
              >
                <span>{t('depositReport.colName')}</span>
                <span style={{ textAlign: 'right' }}>{t('depositReport.colJugs')}</span>
                <span style={{ textAlign: 'right' }}>{t('depositReport.colDeposit')}</span>
                <span style={{ textAlign: 'right' }}>{t('depositReport.colLastMove')}</span>
              </div>
              {data.items.map((i) => (
                <div
                  key={i.customerId}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 110px 130px 130px',
                    padding: '8px 0',
                    borderTop: '1px solid var(--border)',
                    fontSize: 13,
                    alignItems: 'center',
                    opacity: i.jugsOnHand !== 0 ? 1 : 0.6,
                  }}
                >
                  <span style={{ fontWeight: 500 }}>{i.name}</span>
                  {i.jugsOnHand !== 0 ? (
                    <span style={{ textAlign: 'right' }}>{formatNumber(i.jugsOnHand)}</span>
                  ) : (
                    <span style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>
                      {t('depositReport.noJugs')}
                    </span>
                  )}
                  <span style={{ textAlign: 'right', fontWeight: 600 }}>
                    {formatMoney(i.depositHeld)}
                  </span>
                  <span style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>
                    {i.lastMoveAt ? formatDate(i.lastMoveAt) : '—'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function Empty() {
  return (
    <p
      style={{
        fontSize: 13.5,
        color: 'var(--text-secondary)',
        padding: '30px 0',
        textAlign: 'center',
      }}
    >
      {t('rep.noData')}
    </p>
  );
}

/**
 * Kaynak × kanal tablosu. Satır = kaynak (Abant/Hayat/Bizim + Toplam). Alt satırlar kanal.
 * Sütunlar: sipariş (delivered), iptal, panelde açık, ciro.
 *
 * Ciro YALNIZ teslim edilenlerin toplamı; iptal/işlemde ayrı sütunlarda sayılır. Sunucu
 * `Prisma.Decimal` ile hesaplar; burada yalnız çizilir (kural 4).
 */
function SourceChannelTable({ rows }: { rows: SourceChannelRow[] }) {
  if (rows.length === 0) return <Empty />;
  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.4fr 70px 110px 110px 110px',
          padding: '0 0 6px',
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--text-secondary)',
        }}
      >
        <span>{t('rep.sourceLabel')}</span>
        <span style={{ textAlign: 'right' }}>{t('rep.colDelivered')}</span>
        <span style={{ textAlign: 'right' }}>{t('rep.colCancelled')}</span>
        <span style={{ textAlign: 'right' }}>{t('rep.colPending')}</span>
        <span style={{ textAlign: 'right' }}>{t('rep.colChannelAmount')}</span>
      </div>
      {rows.map((r) => (
        <div key={r.source}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1.4fr 70px 110px 110px 110px',
              padding: '10px 0 4px',
              fontSize: 13.5,
              fontWeight: r.source === 'TOTAL' ? 700 : 600,
              borderTop:
                r.source === 'TOTAL' ? '2px solid var(--border)' : '1px solid var(--border)',
              marginTop: r.source === 'TOTAL' ? 6 : 0,
            }}
          >
            <span>{sourceLabel(r.source)}</span>
            <span style={{ textAlign: 'right' }}>{r.delivered}</span>
            <span style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>
              {r.cancelled}
            </span>
            <span style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{r.pending}</span>
            <span style={{ textAlign: 'right', fontWeight: 600 }}>{formatMoney(r.revenue)}</span>
          </div>
          {r.channels.map((c) => (
            <div
              key={c.channel}
              style={{
                display: 'grid',
                gridTemplateColumns: '1.4fr 70px 110px 110px 110px',
                padding: '4px 0',
                fontSize: 12.5,
                color: 'var(--text-secondary)',
              }}
            >
              <span style={{ paddingLeft: 18 }}>
                <span
                  style={{
                    display: 'inline-block',
                    width: 8,
                    height: 8,
                    borderRadius: 2,
                    background: CHANNEL_COLOR[c.channel] ?? 'var(--primary)',
                    marginRight: 8,
                    verticalAlign: 'middle',
                  }}
                />
                {t(`orderChannel.${c.channel}` as Parameters<typeof t>[0])}
              </span>
              <span style={{ textAlign: 'right' }}>{c.delivered}</span>
              <span style={{ textAlign: 'right' }}>{c.cancelled}</span>
              <span style={{ textAlign: 'right' }}>{c.pending}</span>
              <span style={{ textAlign: 'right' }}>{formatMoney(c.revenue)}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Ürün tablosu: her ürün için toplam/teslim adet + ciro + kaynak kırılımı. Sipariş kalemi
 * ürün eşleşmesi olmayan satırlar "Eşleşmemiş ürün" başlığı altında toplanır.
 */
function ProductTable({ rows }: { rows: ProductReportRow[] }) {
  if (rows.length === 0) return <Empty />;
  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '2fr 90px 90px 130px 1.5fr',
          padding: '0 0 6px',
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--text-secondary)',
        }}
      >
        <span>{t('rep.colProduct')}</span>
        <span style={{ textAlign: 'right' }}>{t('rep.colTotalQty')}</span>
        <span style={{ textAlign: 'right' }}>{t('rep.colDeliveredQty')}</span>
        <span style={{ textAlign: 'right' }}>{t('rep.colRevenue')}</span>
        <span style={{ textAlign: 'left', paddingLeft: 12 }}>{t('rep.colSourceBreak')}</span>
      </div>
      {rows.map((r) => (
        <div
          key={r.productId}
          style={{
            display: 'grid',
            gridTemplateColumns: '2fr 90px 90px 130px 1.5fr',
            padding: '8px 0',
            borderTop: '1px solid var(--border)',
            fontSize: 13,
            alignItems: 'center',
          }}
        >
          <span style={{ fontWeight: 500 }}>{r.name}</span>
          <span style={{ textAlign: 'right' }}>{r.totalQty}</span>
          <span style={{ textAlign: 'right', fontWeight: 600 }}>{r.deliveredQty}</span>
          <span style={{ textAlign: 'right', fontWeight: 600 }}>{formatMoney(r.revenue)}</span>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)', paddingLeft: 12 }}>
            {r.sources.map((s) => `${sourceLabel(s.source)} ${s.qty}`).join(' · ')}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Damacana kullanımı: üç kova (19L PC / 19L Cam / Koli) × iki kaynak (bizde / panelde).
 * Panel satırının altında boş alımı bilgisi tek cümleyle belirtilir; sayı uydurulmaz.
 */
function JugsByKindTable({ data }: { data: ReportsResponse['jugsByKind'] | undefined }) {
  if (!data) return <Empty />;
  const ours: JugKindBucket & { emptyReceived: number } = {
    '19L_PC': data.ours['19L_PC'],
    '19L_CAM': data.ours['19L_CAM'],
    KOLI: data.ours.KOLI,
    emptyReceived: data.ours.emptyReceived,
  };
  // `emptyReceived` panelde YOKTUR (boş alımı okunmaz); `Row`'un ortak tipi gereği 0 verilir,
  // `showEmpty={false}` olduğu için Row bu alanı hiç göstermez — tire basar.
  const panel: JugKindBucket & { emptyReceived: number } = {
    '19L_PC': data.panel['19L_PC'],
    '19L_CAM': data.panel['19L_CAM'],
    KOLI: data.panel.KOLI,
    emptyReceived: 0,
  };
  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 110px 110px 110px 130px',
          padding: '0 0 6px',
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--text-secondary)',
        }}
      >
        <span />
        <span style={{ textAlign: 'right' }}>{t('rep.jugKind.19L_PC')}</span>
        <span style={{ textAlign: 'right' }}>{t('rep.jugKind.19L_CAM')}</span>
        <span style={{ textAlign: 'right' }}>{t('rep.jugKind.KOLI')}</span>
        <span style={{ textAlign: 'right' }}>{t('rep.jugsEmptyReceived')}</span>
      </div>
      <Row label={t('rep.jugsOurs')} row={ours} showEmpty />
      <Row label={t('rep.jugsPanel')} row={panel} showEmpty={false} />
      <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8 }}>
        {t('rep.jugsEmptyUnavailable')}
      </p>
    </div>
  );
}

/** JugsByKindTable'ın tek satırı; `showEmpty` paneldeyse yalnız boş hücreler tire olur. */
function Row({
  label,
  row,
  showEmpty,
}: {
  label: string;
  row: JugKindBucket & { emptyReceived: number };
  showEmpty: boolean;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 110px 110px 110px 130px',
        padding: '8px 0',
        borderTop: '1px solid var(--border)',
        fontSize: 13,
      }}
    >
      <span style={{ fontWeight: 600 }}>{label}</span>
      <span style={{ textAlign: 'right' }}>{row['19L_PC']}</span>
      <span style={{ textAlign: 'right' }}>{row['19L_CAM']}</span>
      <span style={{ textAlign: 'right' }}>{row.KOLI}</span>
      <span style={{ textAlign: 'right', color: showEmpty ? undefined : 'var(--text-secondary)' }}>
        {showEmpty ? row.emptyReceived : '—'}
      </span>
    </div>
  );
}

/**
 * Araç tablosu: bizim plakalar + panel araç etiketleri (Abant'ta çalışan kimliği → ad çözümü
 * `useAbantEmployeeNames` ile). Ciro YALNIZ teslim edilen siparişlerden; iptal/işlemde sayaç.
 */
function VehicleTable({ data }: { data: VehicleBreakdownReport | undefined }) {
  const employeeNames = useAbantEmployeeNames();
  if (!data) return <Empty />;
  return (
    <div>
      <div className="section-label" style={{ fontSize: 13, marginBottom: 6 }}>
        {t('rep.vehicleOurs')}
      </div>
      {data.ours.length === 0 ? (
        <p style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{t('rep.noDataShort')}</p>
      ) : (
        <SimpleList
          head={[
            { key: 'plate', label: t('rep.colPlate') },
            { key: 'delivered', label: t('rep.colDelivered'), align: 'right' },
            { key: 'revenue', label: t('rep.colRevenue'), align: 'right' },
          ]}
          rows={data.ours.map((r) => [
            <span key="p">{r.plate}</span>,
            <span key="d" style={{ textAlign: 'right' }}>
              {r.delivered}
            </span>,
            <span key="r" style={{ textAlign: 'right', fontWeight: 600 }}>
              {formatMoney(r.revenue)}
            </span>,
          ])}
        />
      )}
      <div className="section-label" style={{ fontSize: 13, marginTop: 14, marginBottom: 6 }}>
        {t('rep.vehiclePanel')}
      </div>
      {data.panel.length === 0 ? (
        <p style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{t('rep.noDataShort')}</p>
      ) : (
        <SimpleList
          head={[
            { key: 'label', label: t('rep.colLabel') },
            { key: 'delivered', label: t('rep.colDelivered'), align: 'right' },
            { key: 'cancelled', label: t('rep.colCancelled'), align: 'right' },
            { key: 'pending', label: t('rep.colPending'), align: 'right' },
            { key: 'revenue', label: t('rep.colRevenue'), align: 'right' },
          ]}
          rows={data.panel.map((r) => [
            <span key="l">{panelVehicleLabel(r.label, r.source, employeeNames)}</span>,
            <span key="d" style={{ textAlign: 'right' }}>
              {r.delivered}
            </span>,
            <span key="c" style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>
              {r.cancelled}
            </span>,
            <span key="p" style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>
              {r.pending}
            </span>,
            <span key="r" style={{ textAlign: 'right', fontWeight: 600 }}>
              {formatMoney(r.revenue)}
            </span>,
          ])}
        />
      )}
    </div>
  );
}

/**
 * Basit tablo bileşeni: başlık satırı + gövde. Diğer rapor tabloları kendi gridTemplateColumns
 * değerlerini yazıyor; bu liste tekrar eden stili paylaşır.
 */
function SimpleList({
  head,
  rows,
}: {
  head: { key: string; label: string; align?: 'right' | 'left' }[];
  rows: React.ReactNode[][];
}) {
  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '2fr 90px 90px 90px 130px',
          padding: '0 0 6px',
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--text-secondary)',
        }}
      >
        {head.map((h) => (
          <span key={h.key} style={{ textAlign: h.align ?? 'left', gridColumn: 'auto' }}>
            {h.label}
          </span>
        ))}
      </div>
      {rows.map((cells, i) => (
        <div
          key={i}
          style={{
            display: 'grid',
            gridTemplateColumns: '2fr 90px 90px 90px 130px',
            padding: '8px 0',
            borderTop: '1px solid var(--border)',
            fontSize: 13,
            alignItems: 'center',
          }}
        >
          {cells}
        </div>
      ))}
    </div>
  );
}

/**
 * Abant panel araç etiketini "Ad Soyad"a çevirir. Kayıt yoksa ham kimlik ya da "Abant araç
 * <id>" olarak görünür; hayır deyince boş hücre kalmaz.
 */
function panelVehicleLabel(
  label: string,
  source: string,
  employeeNames: Record<string, string>,
): string {
  if (!label) return '—';
  if (source === 'ABANT_PANEL') {
    const name = employeeNames[label];
    if (name) return `${name} (${label})`;
    return `Abant araç ${label}`;
  }
  return label;
}

/** Kaynak enum'unu kullanıcı metnine çevirir. TOTAL için özel metin. */
function sourceLabel(source: string): string {
  if (source === 'TOTAL') return t('rep.colTotal');
  return t(`orderSource.${source}` as Parameters<typeof t>[0]);
}

/**
 * Tablodan CSV üretir — istemci tarafında, dosya sunucu ucu yerine blob indirilir.
 * Satır sayısı küçük (yüzler); maliyet ihmal edilebilir.
 */
function TableCsvButton({
  filename,
  label,
  description,
  disabled,
  rows,
}: {
  filename: string;
  label: string;
  description: string;
  disabled?: boolean;
  rows: { header: string[]; lines: string[][] };
}) {
  const [busy, setBusy] = useState(false);
  const descId = useId();
  function run(): void {
    if (disabled) return;
    setBusy(true);
    try {
      const csv = toCsvLocal(rows.header, rows.lines);
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 12 }}>
      <button
        className="btn"
        disabled={busy || disabled}
        aria-describedby={descId}
        onClick={run}
        style={{ fontSize: 12.5, alignSelf: 'flex-start' }}
      >
        {label}
      </button>
      <span id={descId} style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
        {description}
      </span>
    </div>
  );
}

/** CSV üretir — sunucu `export.service.ts#toCsv` ile AYNI kuyruk karakterini kullanır. */
function toCsvLocal(header: string[], rows: string[][]): string {
  const cell = (v: string): string => {
    const guarded = /^[=+\-@\t\r]/.test(v) ? `'${v}` : v;
    return /[";\r\n]/.test(guarded) ? `"${guarded.replace(/"/g, '""')}"` : guarded;
  };
  const lines = [header, ...rows].map((r) => r.map(cell).join(';'));
  return lines.join('\r\n') + '\r\n';
}

/** Kaynak × kanal tablosu CSV çıktısı. */
function csvFromSourceChannel(rows: SourceChannelRow[]): { header: string[]; lines: string[][] } {
  const header = [
    t('rep.sourceLabel'),
    t('rep.colChannel'),
    t('rep.colDelivered'),
    t('rep.colCancelled'),
    t('rep.colPending'),
    t('rep.colRevenue'),
  ];
  const lines: string[][] = [];
  for (const r of rows) {
    for (const c of r.channels) {
      lines.push([
        sourceLabel(r.source),
        t(`orderChannel.${c.channel}` as Parameters<typeof t>[0]),
        String(c.delivered),
        String(c.cancelled),
        String(c.pending),
        formatMoney(c.revenue),
      ]);
    }
  }
  return { header, lines };
}

/** Ürün tablosu CSV çıktısı. */
function csvFromProducts(rows: ProductReportRow[]): { header: string[]; lines: string[][] } {
  const header = [
    t('rep.colProduct'),
    t('rep.colTotalQty'),
    t('rep.colDeliveredQty'),
    t('rep.colRevenue'),
    t('rep.colSourceBreak'),
  ];
  return {
    header,
    lines: rows.map((r) => [
      r.name,
      String(r.totalQty),
      String(r.deliveredQty),
      formatMoney(r.revenue),
      r.sources.map((s) => `${sourceLabel(s.source)} ${s.qty}`).join(' · '),
    ]),
  };
}

/** Damacana kullanımı CSV çıktısı. */
function csvFromJugs(data: ReportsResponse['jugsByKind'] | undefined): {
  header: string[];
  lines: string[][];
} {
  const header = [
    t('rep.jugsOurs'),
    t('rep.jugKind.19L_PC'),
    t('rep.jugKind.19L_CAM'),
    t('rep.jugKind.KOLI'),
    t('rep.jugsEmptyReceived'),
  ];
  if (!data) return { header, lines: [] };
  return {
    header,
    lines: [
      [
        t('rep.jugsOurs'),
        String(data.ours['19L_PC']),
        String(data.ours['19L_CAM']),
        String(data.ours.KOLI),
        String(data.ours.emptyReceived),
      ],
      [
        t('rep.jugsPanel'),
        String(data.panel['19L_PC']),
        String(data.panel['19L_CAM']),
        String(data.panel.KOLI),
        '—',
      ],
    ],
  };
}

/** Araç tablosu CSV çıktısı. Abant etiketleri adla yazılmaz (adlar kişisel veri sayılabilir);
 *  ham kimlik veya "Abant araç <id>" korunur. */
function csvFromVehicles(data: VehicleBreakdownReport | undefined): {
  header: string[];
  lines: string[][];
} {
  const header = [
    t('rep.vehicleOurs'),
    t('rep.colPlate'),
    t('rep.colLabel'),
    t('rep.colDelivered'),
    t('rep.colCancelled'),
    t('rep.colPending'),
    t('rep.colRevenue'),
  ];
  const lines: string[][] = [];
  if (!data) return { header, lines };
  for (const r of data.ours) {
    lines.push([
      t('rep.vehicleOurs'),
      r.plate,
      '',
      String(r.delivered),
      '',
      '',
      formatMoney(r.revenue),
    ]);
  }
  for (const r of data.panel) {
    lines.push([
      t('rep.vehiclePanel'),
      '',
      r.label,
      String(r.delivered),
      String(r.cancelled),
      String(r.pending),
      formatMoney(r.revenue),
    ]);
  }
  return { header, lines };
}

// ── Patron özeti (UX-C) ─────────────────────────────────────────────────────

const OWNER_PERIODS: { value: OwnerSummaryPeriodKey; period: PeriodKey }[] = [
  { value: 'today', period: 'today' },
  { value: 'week', period: 'thisWeek' },
  { value: 'month', period: 'thisMonth' },
];

const OWNER_SOURCE_KEY: Record<string, Parameters<typeof t>[0]> = {
  MANUAL: 'rep.ownerSrc.MANUAL',
  GETIR_SU: 'rep.ownerSrc.GETIR_SU',
  HAYAT_PANEL: 'rep.ownerSrc.HAYAT_PANEL',
  ABANT_PANEL: 'rep.ownerSrc.ABANT_PANEL',
};

function ownerSourceLabel(source: string): string {
  const key = OWNER_SOURCE_KEY[source];
  return key ? t(key) : source;
}

/**
 * Patron özeti: bugün / bu hafta / bu ay, TEK istekte (`GET /api/reports/owner-summary`).
 *
 * Patronun soruları sırayla cevaplanır: ne kazandım, kaç damacana sattım, gelen boşlar bizim mi
 * yabancı mı, zimmet var mı, hangi kaynaktan ne kadar, hangi personel ne yapmış. Tüm tutar ve
 * yüzdeler sunucudan gelir; panel hesap yapmaz (kural 4). Aşağıdaki ayrıntılı rapor kendi
 * dönem seçicisiyle çalışır; bu bölüm ondan bağımsızdır.
 */
function OwnerSummarySection() {
  const [key, setKey] = useState<OwnerSummaryPeriodKey>('today');
  const q = useOwnerSummary();
  const p = q.data?.periods?.[key];
  const deposit = q.data?.openDeposit;

  return (
    <section
      className="card"
      data-testid="owner-summary"
      style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 15 }}>{t('rep.ownerTitle')}</strong>
        <span style={{ flex: 1 }} />
        {OWNER_PERIODS.map((o) => (
          <button
            key={o.value}
            type="button"
            data-testid={`owner-period-${o.value}`}
            onClick={() => setKey(o.value)}
            aria-pressed={key === o.value}
            // Aşağıdaki ayrıntılı rapor da "Bu ay" düğmesi taşır; erişilebilir ad ayırt edilir.
            aria-label={`${t('rep.ownerTitle')}: ${periodLabel(o.period)}`}
            style={{
              borderRadius: 'var(--r-pill)',
              padding: '8px 14px',
              fontWeight: 600,
              fontSize: 12.5,
              background: key === o.value ? 'var(--primary)' : 'var(--surface)',
              border: `1px solid ${key === o.value ? 'var(--primary)' : 'var(--border)'}`,
              color: key === o.value ? '#fff' : 'var(--text-secondary)',
            }}
          >
            {periodLabel(o.period)}
          </button>
        ))}
      </div>
      <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: 0 }}>
        {t('rep.ownerIntro')}
      </p>

      {q.isError ? (
        <p role="alert" style={{ fontSize: 13, color: 'var(--danger)', margin: 0 }}>
          {t('rep.ownerLoadError')}
        </p>
      ) : null}

      {p ? (
        <>
          <div className="kpi-grid">
            <Kpi
              label={t('rep.ownerRevenue')}
              value={formatMoney(p.revenue.total)}
              note={t('rep.ownerRevenueNote', {
                ours: formatMoney(p.revenue.ours),
                panel: formatMoney(p.revenue.panelDelivered),
              })}
            />
            <Kpi
              label={t('rep.ownerJugs')}
              value={formatNumber(p.jugs)}
              note={t('rep.ownerJugsNote', { n: formatNumber(p.deliveries) })}
            />
            <Kpi
              label={t('rep.ownerCollections')}
              value={formatMoney(p.collections.cash)}
              note={t('rep.ownerCollectionsNote', {
                card: formatMoney(p.collections.card),
                online: formatMoney(p.collections.online),
                credit: formatMoney(p.collections.credit),
              })}
            />
            <Kpi
              label={t('rep.ownerEmpties')}
              value={formatNumber(p.emptiesReceived.own + p.emptiesReceived.foreign)}
              note={t('rep.ownerEmptiesNote', {
                own: formatNumber(p.emptiesReceived.own),
                foreign: formatNumber(p.emptiesReceived.foreign),
              })}
              accent={p.emptiesReceived.foreign > 0 ? 'warning' : undefined}
            />
            {deposit ? (
              <Kpi
                label={t('rep.ownerDeposit')}
                value={formatNumber(deposit.jugsOnHand)}
                note={`${t('rep.ownerDepositNote', {
                  jugs: formatNumber(deposit.jugsOnHand),
                  money: formatMoney(deposit.depositHeld),
                  n: formatNumber(deposit.customers),
                })} · ${t('rep.ownerDepositHint')}`}
              />
            ) : null}
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>
            {t('rep.ownerCollectionsHint')}
          </p>

          <div
            style={{
              display: 'grid',
              gap: 16,
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            }}
          >
            <div>
              <strong style={{ fontSize: 13 }}>{t('rep.ownerSources')}</strong>
              {p.bySource.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  {t('rep.ownerEmpty')}
                </p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 6 }}>
                  <thead>
                    <tr>
                      <th style={ownerTh('left')}>{t('rep.ownerColSource')}</th>
                      <th style={ownerTh('right')}>{t('rep.ownerColCount')}</th>
                      <th style={ownerTh('right')}>{t('rep.ownerColRevenue')}</th>
                      <th style={ownerTh('right')}>{t('rep.ownerColShare')}</th>
                    </tr>
                  </thead>
                  <tbody data-testid="owner-sources">
                    {p.bySource.map((s) => (
                      <tr key={s.source}>
                        <td style={ownerTd('left')}>{ownerSourceLabel(s.source)}</td>
                        <td style={ownerTd('right')}>{formatNumber(s.delivered)}</td>
                        <td style={ownerTd('right')}>{formatMoney(s.revenue)}</td>
                        <td style={ownerTd('right')}>%{s.sharePct}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div>
              <strong style={{ fontSize: 13 }}>{t('rep.ownerStaff')}</strong>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '2px 0 0' }}>
                {t('rep.ownerStaffHint')}
              </p>
              {p.byDriver.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  {t('rep.ownerEmpty')}
                </p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 6 }}>
                  <thead>
                    <tr>
                      <th style={ownerTh('left')}>{t('rep.ownerColStaff')}</th>
                      <th style={ownerTh('right')}>{t('rep.ownerColCount')}</th>
                      <th style={ownerTh('right')}>{t('rep.ownerColJugs')}</th>
                      <th style={ownerTh('right')}>{t('rep.ownerColCash')}</th>
                    </tr>
                  </thead>
                  <tbody data-testid="owner-staff">
                    {p.byDriver.map((d) => (
                      <tr key={d.driverId}>
                        <td style={ownerTd('left')}>{d.name}</td>
                        <td style={ownerTd('right')}>{formatNumber(d.deliveries)}</td>
                        <td style={ownerTd('right')}>{formatNumber(d.jugs)}</td>
                        <td style={ownerTd('right')}>{formatMoney(d.collected)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <TableCsvButton
            filename={`patron-ozeti-${p.fromDay}_${p.toDay}.csv`}
            label={t('rep.ownerCsv')}
            description={t('rep.ownerCsvHint')}
            rows={csvFromOwnerSummary(p)}
          />
        </>
      ) : null}
    </section>
  );
}

function ownerTh(align: 'left' | 'right'): React.CSSProperties {
  return {
    textAlign: align,
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--text-secondary)',
    padding: '4px 6px',
  };
}

function ownerTd(align: 'left' | 'right'): React.CSSProperties {
  return {
    textAlign: align,
    fontSize: 13.5,
    padding: '6px',
    borderTop: '1px solid var(--border)',
    fontVariantNumeric: 'tabular-nums',
  };
}

/** Patron özeti CSV çıktısı: önce kaynak satırları, sonra personel satırları. */
function csvFromOwnerSummary(p: OwnerSummaryPeriod): { header: string[]; lines: string[][] } {
  const header = [
    t('rep.ownerColSource'),
    t('rep.ownerColCount'),
    t('rep.ownerColRevenue'),
    t('rep.ownerColShare'),
  ];
  const lines: string[][] = p.bySource.map((s) => [
    ownerSourceLabel(s.source),
    String(s.delivered),
    formatMoney(s.revenue),
    `%${s.sharePct}`,
  ]);
  lines.push([]);
  lines.push([
    t('rep.ownerColStaff'),
    t('rep.ownerColCount'),
    t('rep.ownerColJugs'),
    t('rep.ownerColCash'),
  ]);
  for (const d of p.byDriver) {
    lines.push([d.name, String(d.deliveries), String(d.jugs), formatMoney(d.collected)]);
  }
  return { header, lines };
}
